import { QA_API_DOMAIN, fetchWithSupabaseAuth } from "@/api/classifier";

export type CodingSystem = "ICD-10" | "OPCS-4";
export type DiscrepancyKind = "overcoded" | "undercoded" | "duplicated" | "wrong";
export type InvestigationConfidence = "low" | "medium" | "high";
export type InvestigationTrigger = "manual" | "auto";

export interface InvestigationFinding {
  id: string;
  investigationId: string;
  code: string;
  codingSystem: CodingSystem;
  discrepancyKind: DiscrepancyKind;
  suspectedStep: string;
  suspectedPromptName: string | null;
  suspectedPromptVersion: number | null;
  rootCauseCategory: string;
  knownPatternRef: string | null;
  suggestedFix: string;
  confidence: InvestigationConfidence;
  createdAt: string;
}

export interface Investigation {
  id: string;
  encounterId: string;
  inboundClassificationResultId: string;
  runByUserId: string | null;
  triggeredBy: InvestigationTrigger;
  originalLangfuseTraceId: string | null;
  investigatorLangfuseTraceId: string | null;
  investigatorRunId: string | null;
  summaryMarkdown: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface RunInvestigationResponse {
  investigationId: string;
  findingsCount: number;
  summaryMarkdown: string;
  runId: string;
  investigatorLangfuseTraceId: string | null;
}

export interface LatestInvestigationResponse {
  investigation: Investigation | null;
  findings: InvestigationFinding[];
}

const INVESTIGATIONS_URL = `${QA_API_DOMAIN}/investigations`;

async function unwrap<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(body.error ?? body.message ?? `${fallback} (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export interface RunInvestigationOptions {
  /** Coding systems to review. Defaults to both when omitted. Must be non-empty when provided. */
  codingSystems?: CodingSystem[];
  /**
   * Optional caller-supplied run id (UUID). Lets the UI render a Mastra
   * Cloud deep-link before the workflow finishes. Server falls back to a
   * random UUID when omitted.
   */
  runId?: string;
}

export async function runInvestigation(
  encounterId: string,
  options: RunInvestigationOptions = {},
): Promise<RunInvestigationResponse> {
  const body: Record<string, unknown> = { encounterId };
  if (options.codingSystems && options.codingSystems.length > 0) {
    body.codingSystems = options.codingSystems;
  }
  if (options.runId) {
    body.runId = options.runId;
  }
  const res = await fetchWithSupabaseAuth(INVESTIGATIONS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return unwrap<RunInvestigationResponse>(res, "Failed to run investigator");
}

export async function getLatestInvestigation(encounterId: string): Promise<LatestInvestigationResponse> {
  const params = new URLSearchParams({ encounterId });
  const res = await fetchWithSupabaseAuth(`${INVESTIGATIONS_URL}?${params.toString()}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  return unwrap<LatestInvestigationResponse>(res, "Failed to load investigation");
}
